// Cancellation flow models for the SaaS billing prototype.
// See plans/saas-billing/05-cancel-subscription.md for canonical types.
//
// `RetentionOffer` itself is canonicalized in retention-offer.model.ts (#01);
// #05 owns the UI-side max-2 array rule and the policy that maps reasons to
// offers (rendered server-side in previewCancellation, so the UI just renders
// what the preview returns).

import { RetentionOffer, RetentionOfferType } from './retention-offer.model';
import { TierCode } from './subscription.model';

/**
 * Short, honest list of cancellation reasons. Kept in lock-step with the
 * server-side enum (deferred-impl) so analytics is consistent.
 */
export type CancellationReason =
  | 'too_expensive'
  | 'not_using'
  | 'missing_feature'
  | 'switching'
  | 'business_closed'
  | 'other';

/**
 * Preview returned by previewCancellation. Drives Step 2 (offers) and
 * Step 3 (confirmation copy). All money values in minor units; `endOfPeriod`
 * is ISO 8601.
 *
 * On past_due, `endOfPeriod` is the LAST PAID cycle end (historical date);
 * the copy reflects that.
 */
export interface CancellationPreview {
  reason: CancellationReason;
  endOfPeriod: string;                 // ISO 8601 - last access date
  isAnnual: boolean;
  isPastDue: boolean;
  topUpBalanceAtCancel: number;        // Forfeit at end-of-period
  cycleCreditsRemaining: number;       // allocated - used - reserved (cycle-only)
  // UI enforces max length 2; server is responsible for never returning more.
  offers: RetentionOffer[];
  // True when the tenant hit the 1-per-12-month retention offer cap and the
  // server therefore returned an empty offers array. UI can use this to skip
  // re-rendering or to surface a hint to reviewers in dev mode.
  retentionOffersRateLimited: boolean;
  // Solo's cancel rule (ADO 19427). period_end is today's behaviour; now ends access on confirm.
  accessEnds?: 'period_end' | 'now';
}

/**
 * Input to commitCancellation. `offerOutcome` is intentionally narrow:
 * 'accepted' is not reachable on this path (acceptance routes through
 * acceptRetentionOffer and aborts the cancellation).
 */
export interface CancellationInput {
  reason: CancellationReason;
  freeText: string | null;
  offerOutcome: 'declined' | 'not_shown';
  offeredRetentionOffers?: RetentionOffer[];
  acceptedRetentionOfferId?: string | null;
}

/**
 * Typed error union returned by previewCancellation / commitCancellation.
 */
export type CancellationError =
  | 'CANCEL_INVALID_REASON'
  | 'CANCEL_NO_SUBSCRIPTION'
  | 'CANCEL_ALREADY_CANCELLED';

/**
 * Typed error union returned by reactivate.
 */
export type ReactivateError =
  | 'GRACE_EXPIRED'
  | 'NOT_CANCELLED'
  | 'PAYMENT_METHOD_INVALID'
  | 'REACTIVATE_GRACE_PERIOD_EXPIRED'
  | 'REACTIVATE_NOT_CANCELLED'
  | 'REACTIVATE_PAYMENT_METHOD_INVALID';

/**
 * Typed error union returned by acceptRetentionOffer.
 */
export type RetentionOfferError =
  | 'RETENTION_OFFER_RATE_LIMITED'
  | 'RETENTION_OFFER_EXPIRED'
  | 'RETENTION_OFFER_INVALID'
  | 'RETENTION_DOWNGRADE_PENDING_T2';

/**
 * Convenience shape for the payload passed to acceptRetentionOffer. The mock
 * accepts a free-form Record per the spec; this narrower shape documents the
 * known field set that the offers we generate populate.
 */
export interface RetentionOfferPayload {
  // discount-typed offer
  promotionCode?: string;
  percentOff?: number;
  durationInMonths?: number;
  // downgrade-typed offer
  newTier?: TierCode;
  newSeats?: number;
  // help_link / pause
  helpLinkUrl?: string;
}

// Re-export so consumers can pull from one place without juggling imports.
export { RetentionOffer, RetentionOfferType };
